import gsap from "gsap";
import { SplitText } from "gsap/src/SplitText";
import { ScrollTrigger } from "gsap/src/ScrollTrigger";

// import Lenis from "lenis";


gsap.registerPlugin(SplitText, ScrollTrigger);

// Global coarse-pointer / touch detector
let isTouchDevice =
  (window.matchMedia?.('(pointer: coarse)')?.matches ?? false) ||
  ('ontouchstart' in window) ||
  (navigator.maxTouchPoints ?? 0) > 0;

const mq = window.matchMedia?.('(pointer: coarse)');
mq?.addEventListener?.('change', (e) => { isTouchDevice = !!e.matches; });

  

// // ✅ Conditional Lenis initialization: Disable on Safari
// const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
// const lenis = isSafari ? null : new Lenis({
//   duration: 1.5,
//   easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
//   smoothWheel: true,
//   smoothTouch: false
// });

// // ✅ Export Lenis instance for global access
// export { lenis };

// // Animation frame loop to sync Lenis with GSAP
// function raf(time) {
//   if (lenis) lenis.raf(time);
//   requestAnimationFrame(raf);
// }
// requestAnimationFrame(raf);

// // ✅ Fix: Ensure Lenis updates when the page loads and lazy images load
// let resizeTimeout;

// function refreshLenis() {
//   clearTimeout(resizeTimeout); // Cancel previous calls
//   resizeTimeout = setTimeout(() => {
//     requestAnimationFrame(() => {
//       if (lenis) lenis.resize(); // ✅ Recalculate dimensions smoothly
//     });
//   }, 3000); // Adjust delay if necessary
// }

// // ✅ Refresh Lenis when the page fully loads (not on Safari)
// if (!isSafari) {
//   window.addEventListener("load", refreshLenis);
// }



// ✅ Init (called after Swup loads new content)
function init() {
  ScrollTrigger.refresh();
  pinSections();
  ParallaxContent();
  createParallaxEffect();
  animateImageSwipeIn();
  animateAwardTables();
}

// ✅ Destroy before page transition
function destroy() {
  ScrollTrigger.getAll().forEach(trigger => trigger.kill());
}

// === Animation Functions ===
function animateAwardTables() {
  const tables = document.querySelectorAll('.award-table');

  tables.forEach((el) => {
    const image = el.querySelector('img.award-img');
    if (!image) return;

    let setX, setY, activeImage;

    const align = (e) => {
      setX(e.clientX);
      setY(e.clientY);
    };

    const startFollow = () => document.addEventListener('mousemove', align);
    const stopFollow = () => document.removeEventListener('mousemove', align);

    const fade = gsap.to(image, {
      autoAlpha: 1,
      ease: 'none',
      paused: true,
      onReverseComplete: stopFollow,
    });

    el.addEventListener('mouseenter', (e) => {
      fade.play();
      startFollow();
      if (activeImage) {
        gsap.set(image, {
          x: gsap.getProperty(activeImage, 'x'),
          y: gsap.getProperty(activeImage, 'y'),
        });
      }
      activeImage = image;
      setX = gsap.quickTo(image, 'x', { duration: 0.6, ease: 'power3' });
      setY = gsap.quickTo(image, 'y', { duration: 0.6, ease: 'power3' });
      align(e);
    });

    el.addEventListener('mouseleave', () => fade.reverse());
  });
}

function createParallaxEffect() {
  const containers = gsap.utils.toArray('.parallax-img');

  // Skip parallax on touch devices OR when frontend edit mode is active
  const isEditMode = document.body.classList.contains('highlight-editable-fields');

  if (isTouchDevice || isEditMode) {
    containers.forEach(container => {
      const img = container.querySelector('img');
      if (!img) return;

      // Kill any ScrollTriggers bound to this container
      ScrollTrigger.getAll().forEach(trigger => {
        if (trigger.trigger === container) trigger.kill();
      });

      // Remove inline height adjustments and clear transforms
      img.style.removeProperty('height');
      gsap.set(img, { clearProps: 'transform' });
    });

    return; // 🚫 no parallax on touch or edit mode
  }

  // ✅ Desktop / precise pointers only
  containers.forEach(container => {
    const img = container.querySelector('img');

    if (!img) {
      console.warn("No <img> found in", container);
      return;
    }

    const setupParallax = () => {
      const containerHeight = container.offsetHeight;
      const parallaxOffset = containerHeight * 0.15;
      const requiredHeight = containerHeight + parallaxOffset;
      img.style.height = `${requiredHeight}px`;

      // Kill any existing trigger just in case (e.g. after Swup nav)
      ScrollTrigger.getAll().forEach(trigger => {
        if (trigger.trigger === container) trigger.kill();
      });

      gsap.fromTo(
        img,
        { y: -parallaxOffset },
        {
          y: 0,
          ease: "none",
          scrollTrigger: {
            trigger: container,
            start: "top bottom",
            end: "bottom top",
            scrub: 0.5,
            invalidateOnRefresh: true
          }
        }
      );

      ScrollTrigger.refresh();
    };

    if (img.complete && img.naturalHeight !== 0) {
      setupParallax();
    } else {
      img.addEventListener('load', setupParallax, { once: true });
    }
  });
}

function animateImageSwipeIn() {
  const containers = gsap.utils.toArray(".swipe-container, .project-image");

  containers.forEach((container) => {
    gsap.set(container, { overflow: 'hidden' });

    const image = container.querySelector("img");

    if (image) {
      gsap.set(image, { yPercent: 0, scale: 1, opacity: 1 });

      gsap.to(image, {
        yPercent: 0,
        scale: 1.017,
        duration: 1.2,
        ease: "power2.out",
        scrollTrigger: {
          trigger: container,
          start: "top 70%",
          end: "bottom top",
          toggleActions: "play none none none",
          markers: false
        }
      });
    } else {
      console.warn("No image found in swipe container:", container);
    }
  });
}

function pinSections() {
  gsap.utils.toArray('.pinned').forEach(section => {
    const isPhoneDisabled = section.classList.contains('disable-phone');
    if (isTouchDevice && isPhoneDisabled) return;

    gsap.timeline({
      scrollTrigger: {
        trigger: section,
        start: "top top",
        end: "bottom top",
        pin: true,
        pinSpacing: false,
        scrub: true
      }
    });
  });
}

function ParallaxContent() {
  if (isTouchDevice) return;

  gsap.utils.toArray('.parallax-content').forEach(el => {
    const speed = el.dataset.speed ? parseFloat(el.dataset.speed) : -20;

    gsap.to(el, {
      yPercent: speed,
      ease: 'none',
      scrollTrigger: {
        trigger: el,
        start: 'top bottom',
        end: 'bottom top',
        scrub: true
      }
    });
  });
}

// ✅ Export for main.js
export default {
  init,
  destroy
};